/**
 * Account/Team-level module management for administrators.
 * Provides tabbed interface for managing:
 * - Account Modules: Create and manage account-scoped modules (account admins only)
 * - Team Modules: Create and manage team-scoped modules
 * - Config Overrides: Customize action configs for inherited Core/Account modules
 *
 * This is different from Platform administrator management in admin/ModulesTab.tsx
 * which handles Core (platform-level) modules.
 */
import { Box, Settings, Sliders } from "lucide-react";
import { useState } from "react";


import { AccountModulesTab } from "./modules/AccountModulesTab";
import { ConfigOverridesTab } from "./modules/ConfigOverridesTab";
import { TeamModulesTab } from "./modules/TeamModulesTab";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuthStore } from "@/stores/auth";

/**
 * Module Management page for account/team administrators
 * Provides tabbed interface for managing:
 * - Account Modules: Account-level modules (account admins only)
 * - Team Modules: Team-level modules
 * - Config Overrides: Customize inherited module action configs
 */
export function ModuleManagementPage() {
	const { activeTeam, accounts, user } = useAuthStore();

	// Check if user has account admin permissions (wildcard permission)
	// For now, check if user is super_admin or has account admin role
	const isAccountAdmin = user?.role === "super_admin";

	// Default to account tab if no team is selected (allows managing account-level modules)
	const [activeTab, setActiveTab] = useState<"account" | "team" | "overrides">(
		activeTeam ? "team" : "account"
	);

	if (accounts.length === 0) {
		return (
			<div className="p-6">
				<p className="text-muted-foreground">
					No accounts available. Contact your administrator.
				</p>
			</div>
		);
	}

	return (
		<div className="p-6 space-y-6">
			{/* Header */}
			<div>
				<h1 className="text-2xl font-bold">Module Management</h1>
				<p className="text-muted-foreground">
					{activeTeam
						? `Create and manage modules for ${activeTeam.name}`
						: "Create and manage account-level modules"}
				</p>
			</div>

			{/* Tabbed Interface */}
			<Tabs
				value={activeTab}
				onValueChange={(v) => {
					setActiveTab(v as "account" | "team" | "overrides");
				}}
				className="space-y-4"
			>
				<TabsList>
					{isAccountAdmin && (
						<TabsTrigger value="account">
							<Box className="mr-2 h-4 w-4" />
							Account Modules
						</TabsTrigger>
					)}
					<TabsTrigger value="team" disabled={!activeTeam}>
						<Settings className="mr-2 h-4 w-4" />
						Team Modules
					</TabsTrigger>
					<TabsTrigger value="overrides" disabled={!activeTeam}>
						<Sliders className="mr-2 h-4 w-4" />
						Config Overrides
					</TabsTrigger>
				</TabsList>

				{isAccountAdmin && (
					<TabsContent value="account">
						<AccountModulesTab />
					</TabsContent>
				)}

				<TabsContent value="team">
					{activeTeam ? (
						<TeamModulesTab />
					) : (
						<p className="text-muted-foreground">
							Select a team to manage team modules.
						</p>
					)}
				</TabsContent>

				<TabsContent value="overrides">
					{activeTeam ? (
						<ConfigOverridesTab />
					) : (
						<p className="text-muted-foreground">
							Select a team to manage config overrides.
						</p>
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
}
